import { motion } from "framer-motion";
import { MapPin, Clock, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const FeaturedToursSection = () => {
  const { data: tours = [], isLoading } = useQuery({
    queryKey: ["featured-tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*, destinations(title)")
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("sort_order")
        .limit(4);
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (tours.length === 0) return null;

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12"
        >
          <div>
            <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-2">
              Popular Experiences
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
              Featured Tours
            </h2>
          </div>
          <Link to="/tours" className="mt-4 md:mt-0">
            <Button variant="outline" className="gap-2 rounded-lg border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              View All Tours <ChevronRight className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tours.map((tour: any, index: number) => (
            <motion.div
              key={tour.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-white rounded-lg overflow-hidden border border-border hover:shadow-md transition-all duration-300"
            >
              <Link to={`/tours/${tour.slug}`}>
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={(() => {
                      const url = tour.main_image_url || "/placeholder.svg";
                      if (url.includes("images.unsplash.com")) {
                        return url.replace(/\?w=\d+/, "?w=400") + "&auto=format&q=75&fit=crop";
                      }
                      return url;
                    })()}
                    alt={tour.title}
                    width={400}
                    height={300}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {tour.destinations?.title && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-white/90 text-foreground text-xs font-medium px-2 py-1 rounded">
                      <MapPin className="w-3 h-3" />
                      {tour.destinations.title}
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <h3 className="font-display text-base font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {tour.title}
                  </h3>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4">
                    <Clock className="w-4 h-4" />
                    {tour.duration || "Full Day"}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-muted-foreground text-xs">From</span>
                      <p className="text-lg font-bold text-primary">${tour.adult_price || 0}</p>
                    </div>
                    <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg text-xs px-4">
                      Book Now
                    </Button>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedToursSection;
